import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const EMPTY = {
    code: "",
    type: "percent",
    value: 10,
    max_uses: null,
    valid_from: "",
    valid_until: "",
    is_active: true,
    applicable_package_ids: null,
};

function toDatetimeLocal(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// <input type="datetime-local"> gives a naive "local wall-clock" string with
// no timezone offset — sending it to the server as-is gets it silently
// misinterpreted as UTC (shifting it by the admin's UTC offset). `new Date()`
// parses that naive string as local time, so toISOString() converts it
// correctly before it goes over the wire.
function toIsoOrNull(localDatetimeValue) {
    if (!localDatetimeValue) return null;
    const d = new Date(localDatetimeValue);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function AdminCoupons() {
    const [rows, setRows] = useState([]);
    const [packages, setPackages] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/coupons").then(({ data }) => setRows(data));
    useEffect(() => {
        load();
        api.get("/packages?active_only=false").then(({ data }) => setPackages(data));
    }, []);

    const save = async () => {
        try {
            const payload = {
                ...edit,
                valid_from: toIsoOrNull(edit.valid_from),
                valid_until: toIsoOrNull(edit.valid_until),
                applicable_package_ids:
                    edit.applicable_package_ids && edit.applicable_package_ids.length
                        ? edit.applicable_package_ids
                        : null,
            };
            if (edit.id) await api.put(`/coupons/${edit.id}`, payload);
            else await api.post("/coupons", payload);
            toast.success("Saved");
            setEdit(null);
            load();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    err.response?.data?.error ||
                    err.message,
            );
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this coupon?")) return;
        await api.delete(`/coupons/${id}`);
        toast.success("Deleted");
        load();
    };

    const togglePackage = (pkgId) => {
        const current = edit.applicable_package_ids || [];
        const next = current.includes(pkgId)
            ? current.filter((id) => id !== pkgId)
            : [...current, pkgId];
        setEdit({ ...edit, applicable_package_ids: next });
    };

    return (
        <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-coupons">
            <div className="relative overflow-hidden mb-6 p-6 g-sunset text-white shadow-glow-violet">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                <div className="relative flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-white/70">
                            Admin
                        </div>
                        <h1 className="font-display font-black text-4xl tracking-tighter">
                            Offers &amp; Coupons
                        </h1>
                    </div>
                    <button
                        className="bg-white text-orange-700 hover:bg-orange-50 px-5 py-2.5 font-semibold text-sm flex items-center gap-2"
                        onClick={() => setEdit({ ...EMPTY })}
                        data-testid="new-coupon-btn"
                    >
                        <Plus size={15} /> New Coupon
                    </button>
                </div>
            </div>

            <div className="card-surface overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b">
                        <tr className="text-xs uppercase tracking-widest text-zinc-500">
                            <th className="text-left p-3">Code</th>
                            <th className="text-left p-3">Discount</th>
                            <th className="text-left p-3">Used / Max</th>
                            <th className="text-left p-3">Valid until</th>
                            <th className="text-left p-3">Active</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr
                                key={c.id}
                                className="border-b border-zinc-100"
                                data-testid={`coupon-row-${c.id}`}
                            >
                                <td className="p-3 font-mono font-medium">{c.code}</td>
                                <td className="p-3 font-mono">
                                    {c.type === "percent"
                                        ? `${Number(c.value)}%`
                                        : `₹${Number(c.value)} flat`}
                                </td>
                                <td className="p-3 font-mono">
                                    {c.used_count}/{c.max_uses ?? "∞"}
                                </td>
                                <td className="p-3 text-xs text-zinc-500">
                                    {c.valid_until
                                        ? new Date(c.valid_until).toLocaleString()
                                        : "—"}
                                </td>
                                <td className="p-3">
                                    {c.is_active ? (
                                        <span className="text-green-600 text-xs font-bold">
                                            ACTIVE
                                        </span>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">OFF</span>
                                    )}
                                </td>
                                <td className="p-3 text-right">
                                    <button
                                        className="btn-ghost"
                                        onClick={() =>
                                            setEdit({
                                                ...c,
                                                valid_from: toDatetimeLocal(c.valid_from),
                                                valid_until: toDatetimeLocal(c.valid_until),
                                            })
                                        }
                                        data-testid={`coupon-edit-${c.id}`}
                                    >
                                        <PencilSimple size={14} />
                                    </button>
                                    <button
                                        className="btn-ghost text-red-600"
                                        onClick={() => remove(c.id)}
                                        data-testid={`coupon-del-${c.id}`}
                                    >
                                        <Trash size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {edit && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4"
                    data-testid="coupon-modal"
                >
                    <div className="bg-white w-full max-w-md border border-zinc-300 max-h-[90vh] overflow-y-auto">
                        <div className="p-5 border-b bg-zinc-950 text-white flex items-center justify-between">
                            <div className="font-display font-bold text-lg">
                                {edit.id ? "Edit" : "New"} Coupon
                            </div>
                            <button onClick={() => setEdit(null)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="label-swiss">Code</label>
                                <input
                                    className="input-swiss font-mono uppercase"
                                    value={edit.code}
                                    onChange={(e) =>
                                        setEdit({ ...edit, code: e.target.value })
                                    }
                                    onBlur={(e) =>
                                        setEdit({
                                            ...edit,
                                            code: e.target.value.trim().toUpperCase(),
                                        })
                                    }
                                    data-testid="coupon-code-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-swiss">Type</label>
                                    <select
                                        className="input-swiss"
                                        value={edit.type}
                                        onChange={(e) =>
                                            setEdit({ ...edit, type: e.target.value })
                                        }
                                    >
                                        <option value="percent">Percent (%)</option>
                                        <option value="flat">Flat (₹)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label-swiss">
                                        Value {edit.type === "percent" ? "(%)" : "(₹)"}
                                    </label>
                                    <input
                                        type="number"
                                        className="input-swiss font-mono"
                                        value={edit.value}
                                        onChange={(e) =>
                                            setEdit({
                                                ...edit,
                                                value: Number(e.target.value),
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label-swiss">Max uses (blank = unlimited)</label>
                                <input
                                    type="number"
                                    className="input-swiss font-mono"
                                    value={edit.max_uses ?? ""}
                                    onChange={(e) =>
                                        setEdit({
                                            ...edit,
                                            max_uses:
                                                e.target.value === ""
                                                    ? null
                                                    : Number(e.target.value),
                                        })
                                    }
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-swiss">Valid from</label>
                                    <input
                                        type="datetime-local"
                                        className="input-swiss"
                                        value={edit.valid_from || ""}
                                        onChange={(e) =>
                                            setEdit({ ...edit, valid_from: e.target.value })
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="label-swiss">Valid until</label>
                                    <input
                                        type="datetime-local"
                                        className="input-swiss"
                                        value={edit.valid_until || ""}
                                        onChange={(e) =>
                                            setEdit({ ...edit, valid_until: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label-swiss">
                                    Applicable packages (none checked = all packages)
                                </label>
                                <div className="border border-zinc-200 max-h-36 overflow-y-auto p-2 space-y-1">
                                    {packages.map((p) => (
                                        <label
                                            key={p.id}
                                            className="flex items-center gap-2 text-sm"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={(edit.applicable_package_ids || []).includes(
                                                    p.id,
                                                )}
                                                onChange={() => togglePackage(p.id)}
                                            />
                                            {p.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={edit.is_active}
                                    onChange={(e) =>
                                        setEdit({ ...edit, is_active: e.target.checked })
                                    }
                                />
                                Active
                            </label>
                        </div>
                        <div className="p-5 border-t bg-zinc-50 flex justify-end gap-2">
                            <button className="btn-secondary" onClick={() => setEdit(null)}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={save}
                                data-testid="coupon-save-btn"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
